import {
  BufferAttribute,
  Color,
  Group,
  LineSegments,
  Material,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Vector3,
} from 'three';
import { Text } from 'troika-three-text';

import { disposeGeometriesAndMaterials } from '../../../utils/three/dispose';
import { getMaterials } from '../../../utils/three/material';
import { isDefined } from '../../../utils/variables';
import { renderOrders } from '../../render-orders';
import { getConicLength, getCurveConics } from '../design/design-node/utils/conic';
import { extractSkyMapLineStyles, hasSkyMapLineStyles } from '../design/skymap-extras';
import { getDrawingLayer } from '../glb-layers';
import { StyledLines } from './styled-lines';
import { JoinKey, StyledLinesBuilder } from './styled-lines-data';
import { StripePattern } from './styled-segment';

type Primitive = LineSegments | Mesh;

const white = new Color(1, 1, 1);

const getDrawOrder = (object: Object3D): number | undefined => {
  const drawOrder: unknown = (object as Mesh).geometry?.userData?.skymap?.drawOrder;
  return typeof drawOrder === 'number' ? drawOrder : undefined;
};

/**
 * Whether the object is a primitive of a flat drawing (it has a draw order), which
 * applySkyMapDrawing batches into StyledLines. Objects that share its geometry are batched too.
 */
export const isFlatDrawingPrimitive = (object: Object3D) => isDefined(getDrawOrder(object));

const isTextured = (object: Object3D) =>
  getMaterials(object).some((x) => isDefined((x as MeshBasicMaterial).map));

/**
 * Adds the lines or triangles of a primitive to the builder, transformed by the matrix.
 * Widths and stripes are scaled like the positions, and the stripe patterns are added to the
 * patterns of the builder.
 */
function addPrimitive(
  builder: StyledLinesBuilder,
  patterns: StripePattern[],
  object: Primitive,
  matrix: Matrix4,
) {
  const geometry = object.geometry;
  const position = geometry.getAttribute('position') as BufferAttribute;
  const colors = geometry.getAttribute('color') as BufferAttribute | undefined;
  const index = geometry.getIndex();
  const material = getMaterials(object)[0] as
    | (Material & { color?: Color; vertexColors?: boolean })
    | undefined;

  const materialColor = material?.color ?? white;
  const alpha = material && (material.transparent || material.opacity < 1) ? material.opacity : 1;
  const useVertexColors = material?.vertexColors === true && isDefined(colors);
  const scale = new Vector3().setFromMatrixScale(matrix).x;

  const getColor = (vertex: number) =>
    useVertexColors
      ? new Color().fromBufferAttribute(colors, vertex).multiply(materialColor)
      : materialColor;
  const getVertex = (vertex: number) =>
    new Vector3().fromBufferAttribute(position, vertex).applyMatrix4(matrix);

  if (object instanceof Mesh) {
    const count = index?.count ?? position.count;

    for (let i = 0; i + 2 < count; i += 3) {
      const [a, b, c] = [i, i + 1, i + 2].map((x) => (index ? index.getX(x) : x));
      builder.addTriangle(getVertex(a), getVertex(b), getVertex(c), {
        color: getColor(a),
        alpha,
        id: i / 3,
      });
    }

    builder.endGroup();
    return;
  }

  const styles = extractSkyMapLineStyles(geometry);
  const patternOffset = patterns.length;
  patterns.push(...styles.patterns.map((pattern) => pattern.map((length) => length * scale)));

  const getStyle = (key: number) => {
    const stripe = styles.stripes.get(key);
    const width = styles.widths.get(key);

    return {
      width: isDefined(width) ? width * scale : undefined,
      stripe: stripe && {
        pattern: stripe.pattern + patternOffset,
        scale: stripe.scale,
        offset: stripe.offset * scale,
      },
      alpha,
    };
  };

  const count = (index?.count ?? position.count) / 2;

  for (let segment = 0; segment < count; segment++) {
    const a = index ? index.getX(segment * 2) : segment * 2;
    const b = index ? index.getX(segment * 2 + 1) : segment * 2 + 1;
    const start = getVertex(a);
    const end = getVertex(b);
    const style = { ...getStyle(segment), color: getColor(a), id: segment };
    // Indexed lines share the vertex where they join, otherwise they are joined by position.
    const joinKeys: [JoinKey, JoinKey] | undefined = index ? [a, b] : undefined;
    const curve = styles.curves.get(segment);

    if (!curve) {
      builder.addSegment(start, end, style, joinKeys);
      continue;
    }

    const conics = getCurveConics(
      start,
      end,
      'control' in curve
        ? { control: curve.control.clone().applyMatrix4(matrix), weight: curve.weight }
        : curve,
    );

    // A curve in several pieces has its stripes continue along the pieces.
    let offset = 0;

    conics.forEach((conic, i) => {
      const stripe = style.stripe && { ...style.stripe, offset: style.stripe.offset + offset };
      const keys: [JoinKey, JoinKey] | undefined = joinKeys && [
        i === 0 ? joinKeys[0] : `${segment}:${i}`,
        i === conics.length - 1 ? joinKeys[1] : `${segment}:${i + 1}`,
      ];

      builder.addCurve(conic, { ...style, stripe }, keys);

      if (style.stripe) {
        offset += getConicLength(conic);
      }
    });
  }

  for (const vertex of styles.seams.keys()) {
    builder.addSeam(getVertex(vertex));
  }

  builder.endGroup();
}

/**
 * Sets the materials up for drawing in the draw order: without depth writes, and transparent so
 * that all of them are drawn in the same pass, ordered by render order.
 */
function setFlatDrawingMaterials(object: Object3D) {
  for (const material of getMaterials(object)) {
    material.transparent = true;
    material.depthWrite = false;
    material.userData.flatDrawing = true;
  }
}

/**
 * Removes the primitive, and puts an empty group in its place if it is a layer node itself, so that
 * the layer can still be hidden and shown. Returns the object that the primitive's visibility now
 * depends on: the group, or the parent of the primitive.
 */
function removePrimitive(primitive: Primitive): Object3D | null {
  const parent = primitive.parent;

  if (!getDrawingLayer(primitive) || !parent) {
    primitive.removeFromParent();
    return parent;
  }

  const group = new Group();
  group.name = primitive.name;
  group.userData = primitive.userData;
  group.visible = primitive.visible;
  group.position.copy(primitive.position);
  group.quaternion.copy(primitive.quaternion);
  group.scale.copy(primitive.scale);

  if (primitive.children.length > 0) {
    group.add(...primitive.children);
  }

  parent.children.splice(parent.children.indexOf(primitive), 1, group);
  group.parent = parent;
  primitive.parent = null;

  return group;
}

/**
 * Draws a flat drawing, such as a PDF page, in the order of its primitives, since depth can't tell
 * what is on top. Everything is drawn in a few draw calls: the lines, curves and fills in draw
 * order, in StyledLines, split where there is a textured image in between. The texts are on top.
 * The original primitives are removed.
 *
 * The items keep the layer of their primitive: they are hidden with the node that the primitive
 * was in, e.g. a layer of the PDF, see StyledLines.layerObjects.
 */
function batchDrawing(root: Object3D, primitives: Primitive[]) {
  const toRoot = new Matrix4().copy(root.matrixWorld).invert();
  const patterns: StripePattern[] = [];
  const drawn: Object3D[] = [];
  const layers: Object3D[] = [];
  let builder = new StyledLinesBuilder(patterns);

  const finishBuilder = () => {
    for (const data of builder.build()) {
      const lines = new StyledLines(data);
      lines.name = 'Drawing';
      lines.layerObjects = layers;
      root.add(lines);
      drawn.push(lines);
    }

    builder = new StyledLinesBuilder(patterns);
  };

  const setLayer = (layer: Object3D | null) => {
    if (!layer) {
      return;
    }

    const index = layers.indexOf(layer);
    builder.setLayer(index >= 0 ? index : layers.push(layer) - 1);
  };

  for (const primitive of primitives) {
    if (isTextured(primitive)) {
      finishBuilder();
      drawn.push(primitive);
      continue;
    }

    const matrix = toRoot.clone().multiply(primitive.matrixWorld);
    setLayer(removePrimitive(primitive));
    addPrimitive(builder, patterns, primitive, matrix);
    disposeGeometriesAndMaterials(primitive);
  }

  finishBuilder();

  // The draw order is fit between the render orders of drawings and their texts.
  const { lines, text } = renderOrders.drawing;

  drawn.forEach((object, i) => {
    object.renderOrder = lines + ((text - lines) * i) / drawn.length;
    setFlatDrawingMaterials(object);
  });
}

/**
 * Draws the SkyMap line styles (curves, widths and stripes) and flat drawings of a glb:
 *
 * - Primitives with a draw order (a PDF page) are drawn in that order, batched per drawing,
 *   see batchDrawing.
 * - Other lines with line styles are replaced with StyledLines each.
 */
export function applySkyMapDrawing(scene: Object3D) {
  scene.updateMatrixWorld(true);

  const drawings = new Map<Object3D, { primitive: Primitive; drawOrder: number }[]>();
  const styled: LineSegments[] = [];

  scene.traverse((node) => {
    if (!(node instanceof Mesh || node instanceof LineSegments) || node instanceof StyledLines) {
      return;
    }

    const drawOrder = getDrawOrder(node);

    if (isDefined(drawOrder)) {
      // A drawing is all that is drawn in order under a node of the scene.
      let root: Object3D = node;
      while (root.parent && root.parent !== scene) {
        root = root.parent;
      }

      const primitives = drawings.get(root) ?? [];
      primitives.push({ primitive: node, drawOrder });
      drawings.set(root, primitives);
    } else if (!(node instanceof Mesh) && hasSkyMapLineStyles(node.geometry)) {
      styled.push(node);
    }
  });

  for (const [root, primitives] of drawings) {
    batchDrawing(
      root,
      primitives.sort((a, b) => a.drawOrder - b.drawOrder).map((x) => x.primitive),
    );
  }

  for (const object of styled) {
    const patterns: StripePattern[] = [];
    const builder = new StyledLinesBuilder(patterns);
    addPrimitive(builder, patterns, object, new Matrix4());
    const [data] = builder.build();

    // A layer node of a drawing is replaced with a group, so that the layer can still be hidden and
    // shown, e.g. a DWG layer with only lines, see removePrimitive.
    const layer = getDrawingLayer(object) ? removePrimitive(object) : undefined;

    if (data) {
      const lines = new StyledLines(data);
      lines.name = object.name;

      if (layer) {
        layer.add(lines);
      } else {
        lines.visible = object.visible;
        lines.position.copy(object.position);
        lines.quaternion.copy(object.quaternion);
        lines.scale.copy(object.scale);
        object.parent?.add(lines);
      }
    }

    object.removeFromParent();
    disposeGeometriesAndMaterials(object);
  }

  if (drawings.size > 0) {
    scene.traverse((node) => {
      if (node instanceof Text) {
        node.renderOrder = renderOrders.drawing.text;
      }
    });
  }
}
